/**
 * Admin Guard profile (360° view) — Tier 1 expansion.
 *
 * Replaces the V0.1 single-page layout with TabsPills navigation across:
 *   Profile / Personal & contact / Employment / KYC documents / Shift & duty /
 *   Attendance / Payroll (in_house only) / Visitor check-ins / Audit trail
 *
 * Conditional behaviour:
 *   - employment_type=agency hides the Payroll tab AND swaps the Employment
 *     tab's salary/bank/KYC numerics for an agency contract card.
 *   - Photo / ID-proof / Police-verification doc tiles use the auth-blob
 *     pattern (private streams), upload via multipart POST.
 *
 * Backend (locked):
 *   GET    /v1/guards/:id                          → +agency, attendance[30d],
 *                                                    payrolls[6mo], has_* flags
 *   POST/GET /v1/guards/:id/attendance
 *   GET    /v1/guards/:id/attendance/export.csv?from=&to=
 *   POST   /v1/guards/:id/payroll/generate, mark-paid, finalize
 *   GET    /v1/guards/:id/payroll/export.csv
 *   POST   /v1/guards/:id/photo|id-proof|police-verification (multipart)
 *   GET    same routes → stream blob (auth-gated)
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Download,
  FileSignature,
  History,
  IndianRupee,
  Moon,
  Phone,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Sun,
  Tablet,
  Upload,
  UserCircle2,
  Users,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatDate, formatDateTime, todayIsoDate } from '@/lib/format';
import type { GuardShift } from '@hooddesk/shared-types';
import type {
  AttendanceStatus,
  Guard,
  GuardAttendanceRow,
  GuardDutyKind,
  GuardPayrollRow,
  PayrollStatus
} from './_agency-types';

// ───── types ─────

type VisitorStatus = 'pending' | 'approved' | 'denied' | 'checked_in' | 'checked_out' | 'expired';

interface VisitorRow {
  id: string;
  visitor_name: string;
  phone: string;
  visit_kind: string;
  status: VisitorStatus;
  expected_at: string;
  created_at: string;
  checked_in_at: string | null;
  checked_out_at: string | null;
  checked_in_by_guard_id?: string | null;
  checked_out_by_guard_id?: string | null;
}
interface VisitorListResp {
  items: VisitorRow[];
  next_cursor: string | null;
}

interface AuditLogRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_user_id: string | null;
  actor_role: string;
  at: string;
  before: unknown;
  after: unknown;
}
interface AuditLogListResp {
  items: AuditLogRow[];
  next_cursor: string | null;
}

// ───── label maps ─────

const SHIFT_META: Record<GuardShift, { label: string; icon: JSX.Element; tone: string }> = {
  day: {
    label: 'Day shift',
    icon: <Sun className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]'
  },
  night: {
    label: 'Night shift',
    icon: <Moon className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-vt-primary-100 text-vt-primary'
  },
  rotating: {
    label: 'Rotating',
    icon: <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />,
    tone: 'bg-vt-secondary-100 text-vt-secondary'
  }
};

const DUTY_LABEL: Record<GuardDutyKind, string> = {
  gate: 'Gate',
  tower: 'Tower',
  patrol: 'Patrol',
  common_area: 'Common area'
};

const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  half_day: 'Half day',
  leave: 'Leave',
  holiday: 'Holiday',
  overtime: 'Overtime',
  late: 'Late'
};

const ATTENDANCE_VARIANT: Record<AttendanceStatus, 'ok' | 'alert' | 'warn' | 'info' | 'muted'> = {
  present: 'ok',
  absent: 'alert',
  half_day: 'warn',
  leave: 'info',
  holiday: 'muted',
  overtime: 'info',
  late: 'warn'
};

const PAYROLL_STATUS_VARIANT: Record<PayrollStatus, 'muted' | 'info' | 'ok'> = {
  draft: 'muted',
  finalized: 'info',
  paid: 'ok'
};

const VISITOR_STATUS_VARIANT: Record<VisitorStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  pending: 'warn',
  approved: 'info',
  denied: 'alert',
  checked_in: 'ok',
  checked_out: 'muted',
  expired: 'muted'
};

const AUDIT_ACTION_LABEL: Record<string, string> = {
  'guard.create': 'Guard onboarded',
  'guard.update': 'Guard updated',
  'guard.delete': 'Guard removed',
  'guard.assign_duty': 'Duty assigned',
  'guard.attendance_marked': 'Attendance marked',
  'guard.payroll_generated': 'Payroll generated',
  'guard.payroll_paid': 'Payroll paid',
  'guard.photo_uploaded': 'Photo uploaded',
  'guard.id_proof_uploaded': 'ID proof uploaded',
  'guard.police_verification_uploaded': 'Police verification uploaded',
  'panic.raised': 'Panic alert raised'
};

// ───── helpers ─────

function initialsFor(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function monthLabel(m: number, y: number): string {
  const d = new Date(y, m - 1, 1);
  return new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric' }).format(d);
}

/**
 * useGuardBlobUrl — auth-blob fetcher mirroring useFvBlobUrl / useStaffBlobUrl.
 * `cacheBust` is anything that changes when the resource is replaced (we use
 * a `has_*` boolean + `updated_at`). When falsy, no fetch is issued.
 */
function useGuardBlobUrl(
  guardId: string | null | undefined,
  kind: 'photo' | 'id-proof' | 'police-verification',
  cacheBust: string | boolean | null | undefined
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!guardId || !cacheBust) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>(`/guards/${guardId}/${kind}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [guardId, kind, cacheBust]);
  return url;
}

function SectionTitle({
  icon,
  title,
  subtitle,
  action
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-vt-3 flex items-start justify-between gap-vt-3">
      <div className="flex items-start gap-vt-2">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
        <div>
          <h2 className="text-vt-16 font-semibold tracking-[-0.01em] text-vt-text">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-vt-12 text-vt-text-muted">{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function LabelValue({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-vt-12 uppercase tracking-wide text-vt-text-soft">{label}</span>
      <span className="text-vt-13 text-vt-text">{children}</span>
    </div>
  );
}

// ───── doc tile ─────

function DocTile({
  title,
  description,
  icon,
  hasFile,
  previewUrl,
  uploading,
  onUpload,
  onDownload,
  expiryInfo
}: {
  title: string;
  description?: string;
  icon: React.ReactNode;
  hasFile: boolean;
  previewUrl?: string | null;
  uploading: boolean;
  onUpload: (file: File) => void;
  onDownload?: () => void;
  expiryInfo?: { date: string; expiring: boolean } | null;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4">
      <div className="flex items-start gap-vt-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-vt-text">{title}</div>
          {description ? (
            <div className="text-vt-12 text-vt-text-muted">{description}</div>
          ) : null}
        </div>
        {hasFile ? <Badge variant="ok">On file</Badge> : <Badge variant="muted">Missing</Badge>}
      </div>
      {previewUrl ? (
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="flex h-40 items-center justify-center overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface"
        >
          <img
            src={previewUrl}
            alt={title}
            className="max-h-full max-w-full object-contain"
          />
        </a>
      ) : (
        <div className="flex h-20 items-center justify-center rounded-vt-md border border-dashed border-vt-border text-vt-12 text-vt-text-soft">
          No file uploaded yet
        </div>
      )}
      {expiryInfo ? (
        <div
          className={`rounded-vt-md px-vt-3 py-vt-2 text-vt-12 ${
            expiryInfo.expiring
              ? 'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]'
              : 'bg-vt-surface text-vt-text-muted'
          }`}
        >
          {expiryInfo.expiring ? 'Expires soon: ' : 'Expires: '}
          {formatDate(expiryInfo.date)}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-vt-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-1 text-vt-12 text-vt-text hover:bg-vt-surface-2">
          <Upload className="h-3.5 w-3.5" />
          {uploading ? 'Uploading…' : hasFile ? 'Replace' : 'Upload'}
          <input
            type="file"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.currentTarget.value = '';
            }}
          />
        </label>
        {hasFile && onDownload ? (
          <Button variant="ghost" size="sm" onClick={onDownload}>
            <Download className="h-3.5 w-3.5" /> Download
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ───── page ─────

type Tab =
  | 'profile'
  | 'personal'
  | 'employment'
  | 'kyc'
  | 'duty'
  | 'attendance'
  | 'payroll'
  | 'visitors'
  | 'audit';

export function GuardDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('profile');

  // 1. Guard core (with nested gate + tower + agency + attendance + payrolls)
  const guardQ = useQuery({
    queryKey: ['guard-detail', id],
    queryFn: () => get<Guard>(`/guards/${id}`),
    enabled: Boolean(id)
  });
  const guard = guardQ.data;
  const isAgency = guard?.employment_type === 'agency';

  // 2. Visitors page (client-filter to this guard)
  const visitorsQ = useQuery({
    queryKey: ['guard-detail', 'visitors', id],
    queryFn: () =>
      get<VisitorListResp>('/visitors', { params: { limit: 50, sort: 'checked_in_desc' } }),
    enabled: Boolean(id) && tab === 'visitors'
  });
  const myVisitors = useMemo(() => {
    const all = visitorsQ.data?.items ?? [];
    return all
      .filter(
        (v) =>
          v.checked_in_by_guard_id === id || v.checked_out_by_guard_id === id
      )
      .slice(0, 10);
  }, [visitorsQ.data, id]);

  // 3. Audit log scoped to this guard.
  const auditQ = useQuery({
    queryKey: ['guard-audit', id],
    queryFn: () =>
      get<AuditLogListResp>('/audit-log', {
        params: { entity_type: 'guard', entity_id: id, limit: 10 }
      }),
    enabled: Boolean(id) && tab === 'audit'
  });

  // 4. Doc blob URLs — cache-bust on `has_*` + updated_at so a fresh upload
  //    invalidates the previously-loaded blob.
  const photoCacheKey = guard ? `${guard.has_photo ? '1' : '0'}-${guard.updated_at}` : null;
  const idProofCacheKey = guard ? `${guard.has_id_proof ? '1' : '0'}-${guard.updated_at}` : null;
  const policeCacheKey = guard ? `${guard.has_police_verification ? '1' : '0'}-${guard.updated_at}` : null;
  const photoUrl = useGuardBlobUrl(id, 'photo', guard?.has_photo ? photoCacheKey : null);
  const idProofUrl = useGuardBlobUrl(id, 'id-proof', guard?.has_id_proof ? idProofCacheKey : null);
  const policeUrl = useGuardBlobUrl(
    id,
    'police-verification',
    guard?.has_police_verification ? policeCacheKey : null
  );

  // Police-verification expiry warning (within 30 days)
  const policeExpiryInfo = useMemo(() => {
    if (!guard?.police_verification_expiry) return null;
    const ts = new Date(guard.police_verification_expiry).getTime();
    const days = Math.floor((ts - Date.now()) / (24 * 60 * 60 * 1000));
    return { date: guard.police_verification_expiry, expiring: days >= 0 && days <= 30 };
  }, [guard?.police_verification_expiry]);

  // ───── mutations: docs ─────
  const [uploadingKind, setUploadingKind] = useState<null | 'photo' | 'id-proof' | 'police-verification'>(null);
  const uploadDoc = useMutation({
    mutationFn: async ({ kind, file }: { kind: 'photo' | 'id-proof' | 'police-verification'; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/guards/${id}/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onMutate: ({ kind }) => setUploadingKind(kind),
    onSuccess: (_d, { kind }) => {
      toast.success(
        kind === 'photo'
          ? 'Photo uploaded'
          : kind === 'id-proof'
          ? 'ID proof uploaded'
          : 'Police verification uploaded'
      );
      void queryClient.invalidateQueries({ queryKey: ['guard-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
    onSettled: () => setUploadingKind(null)
  });

  const downloadDoc = async (kind: 'photo' | 'id-proof' | 'police-verification'): Promise<void> => {
    try {
      const res = await api.get<Blob>(`/guards/${id}/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = res.data.type.includes('pdf') ? 'pdf' : res.data.type.includes('png') ? 'png' : 'jpg';
      a.download = `guard-${id}-${kind}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  // ───── mutations: attendance ─────
  const markAttendance = useMutation({
    mutationFn: (status: AttendanceStatus) =>
      post<GuardAttendanceRow>(`/guards/${id}/attendance`, {
        date: todayIsoDate(),
        status
      }),
    onSuccess: () => {
      toast.success('Attendance marked');
      void queryClient.invalidateQueries({ queryKey: ['guard-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Attendance CSV export
  const [exporting, setExporting] = useState(false);
  const exportAttendance = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
      const apiPrefix = import.meta.env.VITE_API_PREFIX ?? '/v1';
      const today = new Date();
      const monthAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
      const isoDay = (d: Date): string =>
        new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
      const params = new URLSearchParams({ from: isoDay(monthAgo), to: isoDay(today) });
      const url = `${apiUrl}${apiPrefix}/guards/${id}/attendance/export.csv?${params.toString()}`;
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) {
        toast.error(`Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `guard-${id}-attendance.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  // ───── mutations: payroll ─────
  const generatePayroll = useMutation({
    mutationFn: () => {
      const now = new Date();
      return post<GuardPayrollRow>(`/guards/${id}/payroll/generate`, {
        period_month: now.getMonth() + 1,
        period_year: now.getFullYear()
      });
    },
    onSuccess: () => {
      toast.success('Payroll generated');
      void queryClient.invalidateQueries({ queryKey: ['guard-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const markPayrollPaid = useMutation({
    mutationFn: (payrollId: string) =>
      post<GuardPayrollRow>(`/guard-payrolls/${payrollId}/mark-paid`, {}),
    onSuccess: () => {
      toast.success('Payroll marked paid');
      void queryClient.invalidateQueries({ queryKey: ['guard-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── loading / error states ─────

  if (guardQ.isLoading) {
    return (
      <>
        <PageHeader title="Guard" description="Loading guard profile…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (guardQ.isError || !guard) {
    return (
      <>
        <PageHeader title="Guard" description="Could not load this profile." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Guard not found"
                description={
                  guardQ.error
                    ? getErrorMessage(guardQ.error)
                    : 'This record may have been removed or you may not have access.'
                }
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/guards' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to guards
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const shiftMeta = SHIFT_META[guard.shift];

  // ───── tab options ─────
  const tabOptions = [
    { value: 'profile', label: 'Profile' },
    { value: 'personal', label: 'Personal & contact' },
    { value: 'employment', label: 'Employment' },
    { value: 'kyc', label: 'KYC documents' },
    { value: 'duty', label: 'Shift & duty' },
    { value: 'attendance', label: 'Attendance' },
    ...(isAgency ? [] : [{ value: 'payroll', label: 'Payroll' }]),
    { value: 'visitors', label: 'Visitor check-ins' },
    { value: 'audit', label: 'Audit trail' }
  ];

  // ───── render ─────
  return (
    <>
      <PageHeader
        title={guard.full_name}
        description={`Guard · ${guard.phone}${guard.employee_code ? ` · ${guard.employee_code}` : ''}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/guards' })} title="Back to guards list">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* ── Header card (always visible) ─────────────────────────── */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 md:flex-row md:items-start p-vt-5">
            {/* Passport-style photo (140×170) */}
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={guard.full_name}
                className="shrink-0 rounded-vt-md border border-vt-border object-cover"
                style={{ width: 140, height: 170 }}
              />
            ) : (
              <span
                className="flex shrink-0 items-center justify-center rounded-vt-md border border-vt-border bg-vt-primary-100 text-vt-32 font-semibold text-vt-primary"
                style={{ width: 140, height: 170 }}
                aria-hidden="true"
              >
                {initialsFor(guard.full_name)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                {guard.full_name}
              </h2>
              <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> {guard.phone}
                </span>
                {guard.employee_code ? (
                  <span className="inline-flex items-center gap-1 font-mono"># {guard.employee_code}</span>
                ) : null}
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
                  Onboarded {formatDate(guard.created_at)}
                </span>
              </div>
              <div className="mt-vt-2 flex flex-wrap items-center gap-vt-2">
                <span className={`vt-pill inline-flex items-center gap-1 ${shiftMeta.tone}`}>
                  {shiftMeta.icon}
                  {shiftMeta.label}
                </span>
                {isAgency ? <Badge variant="info">Agency</Badge> : <Badge variant="muted">In-house</Badge>}
                {guard.duty_kind === 'gate' && guard.gate ? (
                  <Badge variant="info">
                    <ShieldCheck className="h-3 w-3" strokeWidth={1.75} />
                    Gate · {guard.gate.name}
                  </Badge>
                ) : guard.duty_kind === 'tower' && guard.tower ? (
                  <Badge variant="info">Tower · {guard.tower.name}</Badge>
                ) : guard.duty_kind ? (
                  <Badge variant="muted">{DUTY_LABEL[guard.duty_kind]}</Badge>
                ) : (
                  <Badge variant="muted">Unassigned</Badge>
                )}
                {guard.active ? <Badge variant="ok">Active</Badge> : <Badge variant="alert">Inactive</Badge>}
                {guard.device_id ? (
                  <Badge variant="muted">
                    <Tablet className="h-3 w-3" strokeWidth={1.75} /> Tablet paired
                  </Badge>
                ) : (
                  <Badge variant="muted">No tablet</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Tabs row ─────────────────────────────────────────────── */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={tabOptions}
              aria-label="Guard sections"
            />
          </CardContent>
        </Card>

        {/* ── Profile tab — overview cards ─────────────────────────── */}
        {tab === 'profile' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<UserCircle2 className="h-4 w-4" strokeWidth={1.75} />}
                title="Profile snapshot"
                subtitle="At-a-glance roster card. Use tabs above for details."
              />
              <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
                <LabelValue label="Full name">{guard.full_name}</LabelValue>
                <LabelValue label="Phone">{guard.phone}</LabelValue>
                <LabelValue label="Employee #">
                  {guard.employee_code ? <span className="font-mono">{guard.employee_code}</span> : '—'}
                </LabelValue>
                <LabelValue label="Employment">{isAgency ? 'Agency' : 'In-house'}</LabelValue>
                <LabelValue label="Shift">{shiftMeta.label}</LabelValue>
                <LabelValue label="Status">{guard.active ? 'Active' : 'Inactive'}</LabelValue>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* ── Personal & contact ───────────────────────────────────── */}
        {tab === 'personal' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<UserCircle2 className="h-4 w-4" strokeWidth={1.75} />}
                title="Personal & contact"
                subtitle="Personal identity and emergency reach."
              />
              <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
                <LabelValue label="Date of birth">
                  {guard.date_of_birth ? formatDate(guard.date_of_birth) : '—'}
                </LabelValue>
                <LabelValue label="Blood group">{guard.blood_group ?? '—'}</LabelValue>
                <LabelValue label="Hire date">
                  {guard.hire_date ? formatDate(guard.hire_date) : '—'}
                </LabelValue>
                <LabelValue label="Emergency contact">
                  {guard.emergency_contact_name ? (
                    <>
                      {guard.emergency_contact_name}
                      {guard.emergency_contact_phone ? ` · ${guard.emergency_contact_phone}` : null}
                    </>
                  ) : (
                    '—'
                  )}
                </LabelValue>
                <LabelValue label="Permanent address">{guard.permanent_address ?? '—'}</LabelValue>
                <LabelValue label="Current address">{guard.current_address ?? '—'}</LabelValue>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* ── Employment ───────────────────────────────────────────── */}
        {tab === 'employment' ? (
          <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
            {isAgency ? (
              <Card className="md:col-span-2">
                <CardContent className="p-vt-5">
                  <SectionTitle
                    icon={<Building2 className="h-4 w-4" strokeWidth={1.75} />}
                    title="Agency contract"
                    subtitle="Outsourced employment — managed by partner agency."
                  />
                  <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
                    <LabelValue label="Agency">
                      {guard.agency ? (
                        <Link
                          to="/agencies/$id"
                          params={{ id: guard.agency.id }}
                          className="font-medium text-vt-primary hover:underline"
                        >
                          {guard.agency.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </LabelValue>
                    <LabelValue label="Agency employee #">
                      {guard.agency_employee_id ? (
                        <span className="font-mono">{guard.agency_employee_id}</span>
                      ) : (
                        '—'
                      )}
                    </LabelValue>
                    <LabelValue label="Hire date">
                      {guard.hire_date ? formatDate(guard.hire_date) : '—'}
                    </LabelValue>
                    <LabelValue label="Contract until">
                      {guard.contract_until ? formatDate(guard.contract_until) : '—'}
                    </LabelValue>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card>
                  <CardContent className="p-vt-5">
                    <SectionTitle
                      icon={<IndianRupee className="h-4 w-4" strokeWidth={1.75} />}
                      title="Salary & statutory"
                    />
                    <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2">
                      <LabelValue label="Base salary">
                        {formatInr(guard.base_salary_inr)}
                      </LabelValue>
                      <LabelValue label="PF account">{guard.pf_account ?? '—'}</LabelValue>
                      <LabelValue label="ESIC account">{guard.esic_account ?? '—'}</LabelValue>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-vt-5">
                    <SectionTitle
                      icon={<Wallet className="h-4 w-4" strokeWidth={1.75} />}
                      title="Bank"
                    />
                    <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2">
                      <LabelValue label="Bank name">{guard.bank_name ?? '—'}</LabelValue>
                      <LabelValue label="A/C last 4">
                        {guard.bank_account_last4 ? (
                          <span className="font-mono">•••• {guard.bank_account_last4}</span>
                        ) : (
                          '—'
                        )}
                      </LabelValue>
                      <LabelValue label="IFSC">
                        {guard.bank_ifsc ? <span className="font-mono">{guard.bank_ifsc}</span> : '—'}
                      </LabelValue>
                    </div>
                  </CardContent>
                </Card>
                <Card className="md:col-span-2">
                  <CardContent className="p-vt-5">
                    <SectionTitle
                      icon={<CreditCard className="h-4 w-4" strokeWidth={1.75} />}
                      title="KYC numbers"
                      subtitle="Only last 4 digits stored for privacy."
                    />
                    <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2">
                      <LabelValue label="PAN last 4">
                        {guard.pan_last4 ? <span className="font-mono">{guard.pan_last4}</span> : '—'}
                      </LabelValue>
                      <LabelValue label="Aadhaar last 4">
                        {guard.aadhaar_last4 ? <span className="font-mono">{guard.aadhaar_last4}</span> : '—'}
                      </LabelValue>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        ) : null}

        {/* ── KYC documents ───────────────────────────────────────── */}
        {tab === 'kyc' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<FileSignature className="h-4 w-4" strokeWidth={1.75} />}
                title="KYC documents"
                subtitle="Photo, ID proof, police verification — auth-gated streams."
              />
              <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
                <DocTile
                  title="Passport photo"
                  description="Used on roster + gate-app login screen."
                  icon={<UserCircle2 className="h-4 w-4" />}
                  hasFile={Boolean(guard.has_photo)}
                  previewUrl={photoUrl}
                  uploading={uploadingKind === 'photo'}
                  onUpload={(file) => uploadDoc.mutate({ kind: 'photo', file })}
                  onDownload={() => void downloadDoc('photo')}
                />
                <DocTile
                  title="ID proof"
                  description="Aadhaar / PAN / driving licence."
                  icon={<CreditCard className="h-4 w-4" />}
                  hasFile={Boolean(guard.has_id_proof)}
                  previewUrl={idProofUrl}
                  uploading={uploadingKind === 'id-proof'}
                  onUpload={(file) => uploadDoc.mutate({ kind: 'id-proof', file })}
                  onDownload={() => void downloadDoc('id-proof')}
                />
                <DocTile
                  title="Police verification"
                  description="Mandatory for guards in most states."
                  icon={<ShieldCheck className="h-4 w-4" />}
                  hasFile={Boolean(guard.has_police_verification)}
                  previewUrl={policeUrl}
                  uploading={uploadingKind === 'police-verification'}
                  onUpload={(file) =>
                    uploadDoc.mutate({ kind: 'police-verification', file })
                  }
                  onDownload={() => void downloadDoc('police-verification')}
                  expiryInfo={policeExpiryInfo}
                />
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* ── Shift & duty ─────────────────────────────────────────── */}
        {tab === 'duty' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<ShieldCheck className="h-4 w-4" strokeWidth={1.75} />}
                title="Shift & duty"
              />
              <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
                <LabelValue label="Shift">{shiftMeta.label}</LabelValue>
                <LabelValue label="Duty kind">
                  {guard.duty_kind ? DUTY_LABEL[guard.duty_kind] : 'Unassigned'}
                </LabelValue>
                <LabelValue label="Posted at">
                  {guard.duty_kind === 'gate' && guard.gate ? (
                    <Link to="/gates" className="font-medium text-vt-primary hover:underline">
                      Gate · {guard.gate.name}
                    </Link>
                  ) : guard.duty_kind === 'tower' && guard.tower ? (
                    <span>Tower · {guard.tower.name}</span>
                  ) : (
                    '—'
                  )}
                </LabelValue>
                <LabelValue label="Tablet device">
                  {guard.device_id ? (
                    <span className="inline-flex items-center gap-1 font-mono text-vt-12">
                      <Tablet className="h-3.5 w-3.5" />
                      {guard.device_id}
                    </span>
                  ) : (
                    <span className="text-vt-text-soft">Unpaired</span>
                  )}
                </LabelValue>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* ── Attendance ───────────────────────────────────────────── */}
        {tab === 'attendance' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<CalendarClock className="h-4 w-4" strokeWidth={1.75} />}
                title="Attendance — last 30 days"
                subtitle="Mark today's status, or export the full window."
                action={
                  <div className="flex items-center gap-vt-2">
                    <Select
                      className="h-8 w-36 text-vt-12"
                      defaultValue="present"
                      id="attendance-status"
                      onChange={(e) => {
                        const val = e.target.value as AttendanceStatus;
                        if (val) markAttendance.mutate(val);
                      }}
                    >
                      <option value="">Mark today as…</option>
                      {(Object.keys(ATTENDANCE_LABEL) as AttendanceStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {ATTENDANCE_LABEL[s]}
                        </option>
                      ))}
                    </Select>
                    <Button variant="secondary" size="sm" onClick={() => void exportAttendance()} disabled={exporting}>
                      <Download className="h-3.5 w-3.5" />
                      {exporting ? 'Exporting…' : 'Export CSV'}
                    </Button>
                  </div>
                }
              />
              {(guard.attendance?.length ?? 0) === 0 ? (
                <p className="text-vt-13 text-vt-text-muted">No attendance recorded in the last 30 days.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(guard.attendance ?? []).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          {formatDate(row.date)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={ATTENDANCE_VARIANT[row.status]}>
                            {ATTENDANCE_LABEL[row.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-vt-text-muted">
                          {row.hours ?? '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {row.notes ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* ── Payroll (in_house only) ──────────────────────────────── */}
        {tab === 'payroll' && !isAgency ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<IndianRupee className="h-4 w-4" strokeWidth={1.75} />}
                title="Payroll — last 6 months"
                subtitle="Generate, finalize, and mark monthly payroll paid."
                action={
                  <Button size="sm" onClick={() => generatePayroll.mutate()} loading={generatePayroll.isPending}>
                    Generate this month
                  </Button>
                }
              />
              {(guard.payrolls?.length ?? 0) === 0 ? (
                <p className="text-vt-13 text-vt-text-muted">No payroll records on file yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Bonus</TableHead>
                      <TableHead className="text-right">Net payable</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(guard.payrolls ?? []).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-vt-text">
                          {monthLabel(p.period_month, p.period_year)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(p.base_salary_inr)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr((p.deductions_inr ?? 0) + (p.pf_inr ?? 0) + (p.esic_inr ?? 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(p.bonus_inr)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(p.net_payable_inr)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={PAYROLL_STATUS_VARIANT[p.status]}>{p.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {p.status !== 'paid' ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => markPayrollPaid.mutate(p.id)}
                              disabled={markPayrollPaid.isPending}
                            >
                              <CheckCircle2 className="h-4 w-4" /> Mark paid
                            </Button>
                          ) : (
                            <span className="text-vt-12 text-vt-text-muted">
                              {p.paid_at ? formatDate(p.paid_at) : 'Paid'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* ── Visitor check-ins ────────────────────────────────────── */}
        {tab === 'visitors' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<Users className="h-4 w-4" strokeWidth={1.75} />}
                title="Visitor check-ins by this guard"
                subtitle={
                  visitorsQ.data
                    ? `${myVisitors.length} recent entries (client-filtered from latest 50)`
                    : 'Visitors logged in / out by this guard'
                }
                action={
                  <Button variant="link" size="sm" asChild>
                    <Link to="/visitor-logs">Open visitor logs →</Link>
                  </Button>
                }
              />
              {visitorsQ.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : visitorsQ.isError ? (
                <p className="text-vt-13 text-vt-alert">{getErrorMessage(visitorsQ.error)}</p>
              ) : myVisitors.length === 0 ? (
                <p className="text-vt-13 text-vt-text-muted">
                  No recent visitor entries attributed to this guard in the last page.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitor</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Checked in</TableHead>
                      <TableHead>Checked out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myVisitors.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-vt-text">{v.visitor_name}</div>
                            <div className="truncate text-vt-12 text-vt-text-muted">{v.phone}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted capitalize">{v.visit_kind}</TableCell>
                        <TableCell>
                          <Badge variant={VISITOR_STATUS_VARIANT[v.status]} className="capitalize">
                            {v.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-vt-text-muted">
                          {v.checked_in_at ? formatDateTime(v.checked_in_at) : '—'}
                        </TableCell>
                        <TableCell className="text-vt-text-muted">
                          {v.checked_out_at ? formatDateTime(v.checked_out_at) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* ── Audit trail ─────────────────────────────────────────── */}
        {tab === 'audit' ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<History className="h-4 w-4" strokeWidth={1.75} />}
                title="Audit trail"
                subtitle="Recent admin actions on this profile (latest 10)"
                action={
                  <Button variant="link" size="sm" asChild>
                    <Link to="/audit-log" search={{ entity_type: 'guard', entity_id: id } as never}>
                      Open full log →
                    </Link>
                  </Button>
                }
              />
              {auditQ.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : auditQ.isError ? (
                <p className="text-vt-13 text-vt-alert">{getErrorMessage(auditQ.error)}</p>
              ) : (auditQ.data?.items.length ?? 0) === 0 ? (
                <p className="text-vt-13 text-vt-text-muted">No audit events on file.</p>
              ) : (
                <div className="flex flex-col divide-y divide-vt-border">
                  {(auditQ.data?.items ?? []).map((row) => (
                    <div key={row.id} className="flex items-start gap-vt-3 py-vt-2">
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-soft" strokeWidth={1.75} />
                      <div className="min-w-0 flex-1">
                        <div className="text-vt-13 font-medium text-vt-text">
                          {AUDIT_ACTION_LABEL[row.action] ?? row.action}
                        </div>
                        <div className="text-vt-12 text-vt-text-muted">
                          {row.actor_role} · {formatDateTime(row.at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  );
}
